import { Buttons, Overlay, Scroller, Slider, VideoPreview } from '@cian/slider';
import * as React from 'react';

import * as styles from './Carousel.css';
import { LazyImage } from './components/LazyImage';
import { PaginationDots } from './components/PaginationDots';
import { Pessimization } from './components/Pessimization';
import { Stub } from './components/Stub';
import { IPessimization } from './types';
import { EType, IMedia } from '../../repositories/dailyrent/entities/media/Media';

interface ICarouselProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  desktopControls?: boolean;
  media: IMedia[];
  pessimization?: IPessimization;
  slotSliderEnd?: React.ReactNode;
  mainImageAlt?: string;
  onSlideChange?(): void;
}

/** Ссылка на ролик YouTube с id ролика – тот же разбор, что у VideoPreview в @cian/slider */
const YOUTUBE_VIDEO_LINK_REGEX =
  /(?:youtube\.com\/\S*(?:(?:\/e(?:mbed))?\/|watch\?(?:\S*?&?v=))|youtu\.be\/)([a-zA-Z0-9_-]{6,11})/;

/**
 * VideoPreview сам строит обложку по ссылке на ролик YouTube, а любую другую ссылку подставит
 * в картинку как есть. Поэтому без превью от бэкенда ссылку на ролик отдаём только тогда,
 * когда VideoPreview извлечёт из неё id ролика. У Kinescope, Rutube и VK остаётся одна кнопка play.
 */
function getVideoPreviewSrc({ previewUrl, url }: IMedia): string {
  if (previewUrl) {
    return previewUrl;
  }

  return YOUTUBE_VIDEO_LINK_REGEX.test(url) ? url : '';
}

export const Carousel: React.FC<ICarouselProps> = ({
  containerRef,
  desktopControls,
  media: mediaRaw,
  pessimization,
  slotSliderEnd,
  mainImageAlt,
  onSlideChange,
}) => {
  const media = React.useMemo(() => {
    const media = mediaRaw;

    if (pessimization && media[0]) {
      media.unshift(media[0]);
    }

    return media;
  }, [mediaRaw, pessimization]);

  if (media.length === 0) {
    return <Stub pessimization={pessimization} slotEnd={slotSliderEnd} />;
  }

  return (
    <Slider onSlideChange={onSlideChange}>
      <Scroller>
        {media.map((m, index) => {
          const key = `slide_${index}`;
          let slide: React.ReactNode = null;

          switch (m.type) {
            case EType.Layout:
            case EType.Photo:
              slide = (
                <LazyImage
                  containerRef={containerRef}
                  key={key}
                  style={{ objectFit: m.type === EType.Layout ? 'contain' : undefined }}
                  src={m.previewUrl}
                  alt={mainImageAlt}
                />
              );
              break;
            case EType.Video:
              slide = <VideoPreview key={key} src={getVideoPreviewSrc(m)} />;
              break;
          }

          if (index === 0) {
            if (pessimization) {
              return (
                <Pessimization key={key} pessimization={pessimization}>
                  {slide}
                </Pessimization>
              );
            }
          }

          return slide;
        })}
      </Scroller>
      <Overlay hideable={false}>
        <div className={styles['controls']}>
          {desktopControls && <Buttons />}
          <PaginationDots />
        </div>
      </Overlay>
      {slotSliderEnd}
    </Slider>
  );
};
